import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MODAL_STATUS_OPTIONS } from '../utils';

export function InvoiceFilterModal({ open, onClose, value, emptyValue, onApply }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    if (open) setDraft(value);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleStatus(key) {
    setDraft((prev) => ({
      ...prev,
      statuses: prev.statuses.includes(key)
        ? prev.statuses.filter((s) => s !== key)
        : [...prev.statuses, key],
    }));
  }

  function handleApply() { onApply(draft); onClose(); }
  function handleReset() { onApply(emptyValue); onClose(); }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-[480px] max-h-[90vh] overflow-y-auto p-6 space-y-5 z-10">

        <div className="flex items-center justify-between">
          <h3 className="text-[15px] font-semibold text-slate-800">{t('invoices.adv_filter_title')}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition">
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>

        <div>
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
            {t('invoices.adv_filter_status')}
          </p>
          <div className="flex flex-wrap gap-2">
            {MODAL_STATUS_OPTIONS.map(({ key, labelKey }) => (
              <button
                key={key}
                onClick={() => toggleStatus(key)}
                className={`px-3 py-1.5 rounded-lg text-[12.5px] font-medium border transition ${
                  draft.statuses.includes(key)
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                }`}
              >
                {t(`invoices.${labelKey}`)}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
            {t('invoices.adv_filter_amount')}
          </p>
          <div className="flex items-center gap-2">
            <input
              type="number" min="0"
              placeholder={t('invoices.adv_filter_amount_min')}
              value={draft.minAmount}
              onChange={(e) => setDraft((p) => ({ ...p, minAmount: e.target.value }))}
              className="flex-1 h-9 px-3 border border-slate-200 rounded-lg text-[12.5px] outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-50 transition"
            />
            <span className="text-slate-300 text-[14px]">—</span>
            <input
              type="number" min="0"
              placeholder={t('invoices.adv_filter_amount_max')}
              value={draft.maxAmount}
              onChange={(e) => setDraft((p) => ({ ...p, maxAmount: e.target.value }))}
              className="flex-1 h-9 px-3 border border-slate-200 rounded-lg text-[12.5px] outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-50 transition"
            />
          </div>
        </div>

        <div>
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
            {t('invoices.adv_filter_delay')}
          </p>
          <div className="flex items-center gap-2">
            <input
              type="number" min="0"
              placeholder={t('invoices.adv_filter_delay_min')}
              value={draft.minDelay}
              onChange={(e) => setDraft((p) => ({ ...p, minDelay: e.target.value }))}
              className="flex-1 h-9 px-3 border border-slate-200 rounded-lg text-[12.5px] outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-50 transition"
            />
            <span className="text-slate-300 text-[14px]">—</span>
            <input
              type="number" min="0"
              placeholder={t('invoices.adv_filter_delay_max')}
              value={draft.maxDelay}
              onChange={(e) => setDraft((p) => ({ ...p, maxDelay: e.target.value }))}
              className="flex-1 h-9 px-3 border border-slate-200 rounded-lg text-[12.5px] outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-50 transition"
            />
          </div>
        </div>

        <div>
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
            {t('invoices.adv_filter_date')}
          </p>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={draft.dateFrom}
              onChange={(e) => setDraft((p) => ({ ...p, dateFrom: e.target.value }))}
              className="flex-1 h-9 px-3 border border-slate-200 rounded-lg text-[12.5px] outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-50 transition"
            />
            <span className="text-slate-300 text-[14px]">—</span>
            <input
              type="date"
              value={draft.dateTo}
              onChange={(e) => setDraft((p) => ({ ...p, dateTo: e.target.value }))}
              className="flex-1 h-9 px-3 border border-slate-200 rounded-lg text-[12.5px] outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-50 transition"
            />
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          <button
            onClick={handleReset}
            className="text-[12.5px] font-medium text-slate-400 hover:text-slate-600 transition"
          >
            {t('invoices.adv_filter_reset')}
          </button>
          <button
            onClick={handleApply}
            className="h-9 px-5 rounded-lg bg-primary text-white text-[12.5px] font-semibold hover:opacity-90 transition"
          >
            {t('invoices.adv_filter_apply')}
          </button>
        </div>
      </div>
    </div>
  );
}
